import { Button, Image, Input, Table, Pagination, Typography } from 'antd';
import ButtonExport, { ExportOption, MultiSheetData } from 'components/ButtonExport/ButtonExport';
import React, { useMemo, useState } from 'react';
import './index.css';
import { ColumnGroupType, ColumnType } from 'antd/es/table';
import { ExpandableConfig } from 'antd/es/table/interface';
import PREV_ICON from './assets/prev.svg';
import NEXT_ICON from './assets/next.svg';
import LUPA_ICON from './assets/lupa.svg';
import { SizeType } from 'types/field';

const column_exclud = ['acciones'];

type KeysOfK<K> = keyof K;

interface IProps<K> {
  data: Partial<K>[];
  columns: (ColumnType<Partial<K>> | ColumnGroupType<Partial<K>>)[];
  loadingTable?: boolean;
  paperSheetSizePdf?: any;
  exportOptions?: ExportOption;
  nameReport?: string;
  multiSheetData?: MultiSheetData[];
  expandable?: ExpandableConfig<Partial<K>> | undefined;
  extraHeader?: React.ReactNode;
  titleTable?: string;
  iconTable?: React.ReactNode;
  columnsToExport?: (ColumnType<Partial<K>> | ColumnGroupType<Partial<K>>)[];
  extraFilter?: React.ReactNode;
  excludeColumnsFields?: string[];
  tableSize?: SizeType;
  showSearch?: boolean;
  filterOptions?: React.ReactNode;
  extraComponen?: React.ReactNode;
  rowSelectionOn?: boolean;
  onSelectChangeRow?: (rowId: any, rest: any) => void;
  handleIdRecord?: (record: any, index: any) => void;
  idRecord?: any;
  keyId?: string;
}

const DataTableX = <K,>({
  loadingTable = false,
  exportOptions,
  nameReport = 'REPORTE',
  tableSize = 'large',
  multiSheetData,
  extraFilter,
  expandable,
  extraHeader,
  titleTable,
  excludeColumnsFields = [],
  showSearch = true,
  filterOptions,
  rowSelectionOn = false,
  onSelectChangeRow,
  handleIdRecord,
  keyId = 'id',
  idRecord,
  extraComponen,
  ...props
}: IProps<K>) => {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handlerText = (text: string) => {
    setFilterText(text);
  };

  const dataInTable = useMemo(() => {
    if (!filterText.trim()) {
      return props.data;
    }

    return props.data.filter((dato: any) => {
      return Object.values(dato).some((value) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return `${value}`.toLowerCase().includes(filterText.toLowerCase());
        }
        return false;
      });
    });
  }, [filterText, props.data]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return dataInTable.slice(startIndex, endIndex);
  }, [currentPage, pageSize, dataInTable]);

  return (
    <div className="datatablex-container">
      {(titleTable || extraFilter || showSearch || extraHeader) && (
        <div className="container-header-table">
          {titleTable && (
            <div className="container-title-table">
              {props.iconTable && props.iconTable}
              <Typography className="title-table">{titleTable}</Typography>
            </div>
          )}
          <section className="container-filters">
            <div className="container-extra-filter">{extraFilter && extraFilter}</div>
            {showSearch && (
              <div className="container-search-input">
                <Input
                  placeholder="Buscar..."
                  rootClassName="input-search"
                  value={filterText}
                  onChange={(e) => {
                    if (e.target.value?.length > 150) return;
                    handlerText(e.target.value);
                  }}
                />
                <Button className="btn-lupa" icon={<Image height={15} preview={false} src={LUPA_ICON} />} />
              </div>
            )}
          </section>
          <div className="container-extra-header">{extraHeader && extraHeader}</div>
        </div>
      )}
      {filterOptions}

      {/* 🌟 Contenedor que da scroll horizontal y padding */}
      <div className="datatablex-scroll-container">
        <Table
          size={tableSize}
          footer={() => (
            <>
              {extraComponen}
              <div className="datatablex-footer">
                <div />
                <Pagination
                  rootClassName="tablex"
                  current={currentPage}
                  pageSize={pageSize}
                  showLessItems={true}
                  total={dataInTable.length}
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                  pageSizeOptions={['10', '20', '50', '100']}
                  showSizeChanger
                  prevIcon={<Image src={PREV_ICON} height={25} preview={false} />}
                  nextIcon={<Image src={NEXT_ICON} height={25} preview={false} />}
                />
                <ButtonExport
                  paperSheetSizePdf={props.paperSheetSizePdf}
                  columns={
                    props.columnsToExport
                      ? props.columnsToExport.filter(
                          (col) =>
                            ![...column_exclud, ...excludeColumnsFields].includes(
                              String(col.title).toLowerCase()
                            )
                        )
                      : props.columns.filter(
                          (col) =>
                            ![...column_exclud, ...excludeColumnsFields].includes(
                              String(col.title).toLowerCase()
                            )
                        )
                  }
                  dataSource={props.data}
                  exportOptions={exportOptions}
                  sheetName={nameReport}
                  multiSheetData={multiSheetData}
                />
              </div>
            </>
          )}
          style={{
            width: '100%',
            minWidth: 600,
          }}
          onRow={
            handleIdRecord
              ? (record: any, index) => ({
                  onClick: () => {
                    if (!idRecord) {
                      handleIdRecord(record[keyId], record);
                    }
                  },
                })
              : undefined
          }
          loading={loadingTable}
          dataSource={paginatedData}
          rowSelection={
            rowSelectionOn
              ? {
                  selectedRowKeys: idRecord,
                  onChange: onSelectChangeRow
                    ? (rowId, record) => onSelectChangeRow(rowId, record)
                    : undefined,
                  type: 'checkbox',
                  columnWidth: 48,
                }
              : undefined
          }
          rowKey={keyId}
          pagination={false}
          expandable={expandable}
          columns={props.columns}
          scroll={{
            x: 'max-content', // 👈 Evita columnas apretadas
            y: 380,
          }}
          showSorterTooltip={{ title: undefined }}
        />
      </div>
    </div>
  );
};

export default DataTableX;
